import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
// utils
import localStorageAvailable from '@/utils/localStorageAvailable';
//
import { defaultLang } from '@/locales/config-lang';

// ----------------------------------------------------------------------
// Translations are code-split: each language is a separate chunk and only
// the active language (+ fallback) is loaded, instead of bundling all four
// (~840 lines each) into the main bundle. initI18n() is awaited in main.jsx
// before the app renders, so translations are always present at first paint.

const loaders = {
  en: () => import('@/locales/langs/en'),
  ru: () => import('@/locales/langs/ru'),
  tr: () => import('@/locales/langs/tr'),
  uz: () => import('@/locales/langs/uz'),
};

/**
 * Load a language's translation bundle into i18n if not already present.
 * Call this before switching languages so the strings are ready on render.
 */
export async function ensureLanguage(lng) {
  if (!loaders[lng] || i18n.hasResourceBundle(lng, 'translations')) {
    return;
  }
  const module = await loaders[lng]();
  i18n.addResourceBundle(lng, 'translations', module.default, true, true);
}

function detectLanguage() {
  if (localStorageAvailable()) {
    return localStorage.getItem('i18nextLng') || defaultLang.value;
  }
  return defaultLang.value;
}

let initPromise;

/**
 * Initialize i18n and load the active language (+ fallback) bundles.
 * Idempotent — safe to call more than once.
 */
export function initI18n() {
  if (initPromise) {
    return initPromise;
  }

  const lng = detectLanguage();

  initPromise = i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources: {},
      lng,
      fallbackLng: defaultLang.value,
      debug: false,
      ns: ['translations'],
      defaultNS: 'translations',
      interpolation: {
        escapeValue: false,
      },
      react: {
        // We gate rendering on initI18n(), so Suspense isn't needed
        useSuspense: false,
      },
    })
    .then(async () => {
      await ensureLanguage(lng);
      if (lng !== defaultLang.value) {
        await ensureLanguage(defaultLang.value);
      }
      return i18n;
    });

  return initPromise;
}

export default i18n;
